import { Component, Input, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { NgFor, NgIf, DatePipe, CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { StudentComment, COMMENT_CATEGORIES } from '../../../../models/StudentComment';
import { StudentCommentService } from '../../../../services/student-comment.service';
import { ToastrService } from 'ngx-toastr';
import { AuthStateService } from '../../../../core/states/auth-state.service';
import { GenericForm, GenericFormField } from '../../../../models/GenericCRUD/GenericForm';

@Component({
  selector: 'app-student-comments-list',
  standalone: true,
  imports: [NgFor, NgIf, DatePipe, CommonModule, FormsModule],
  templateUrl: './student-comments-list.component.html',
  styleUrl: './student-comments-list.component.css'
})
export class StudentCommentsListComponent implements OnInit, OnChanges {
  @Input() studentId!: number;
  @Input() productId: number | null = null;

  comments: StudentComment[] = [];
  isLoading: boolean = false;
  showGenericDialog: boolean = false;
  currentGenericForm: GenericForm | null = null;
  genericFormInitialData: any = null;
  editingComment: StudentComment | null = null;

  categories = COMMENT_CATEGORIES;
  currentUserId: number | null = null;

  constructor(
    private commentService: StudentCommentService,
    private toastr: ToastrService,
    private authState: AuthStateService
  ) {
    this.currentUserId = this.authState.currentUserValue?.profile.id || null;
  }

  ngOnInit(): void {
    if (!this.studentId) {
      console.error('StudentCommentsListComponent: studentId is required but was not provided');
      return;
    }
    this.loadComments();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['productId'] && !changes['productId'].firstChange && this.studentId) {
      this.loadComments();
    }
  }

  loadComments(): void {
    if (!this.studentId) {
      console.error('Cannot load comments without studentId');
      return;
    }

    this.isLoading = true;
    this.commentService.getStudentComments(this.studentId, this.productId).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.comments = response.data;
        }
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error loading comments:', error);
        this.toastr.error('Error al cargar los comentarios', 'Error');
        this.isLoading = false;
      }
    });
  }

  private getCategoryFormFields(includeHidden: boolean = false): GenericFormField[] {
    const fields: GenericFormField[] = [
      { 
        name: 'category', 
        type: 'select', 
        label: 'Categoría', 
        validations: 'required',
        optionsList: [
          { id: 'general', name: 'General' },
          { id: 'clase_teorica', name: 'Clase Teórica' },
          { id: 'clase_practica', name: 'Clase Práctica' },
          { id: 'examen', name: 'Examen' }
        ],
        singleSelection: true,
        colspan: 2
      },
      { 
        name: 'comment', 
        type: 'textarea', 
        label: 'Comentario', 
        validations: 'required',
        colspan: 2
      }
    ];

    if (includeHidden) {
      fields.push(
        { name: 'student_id', type: 'hidden', label: '', validations: '', colspan: 1 },
        { name: 'teacher_id', type: 'hidden', label: '', validations: '', colspan: 1 }
      );
    }

    return fields;
  }

  openAddModal(): void {
    this.editingComment = null;
    this.genericFormInitialData = {
      student_id: this.studentId,
      product_id: this.productId,
      teacher_id: this.currentUserId,
      category: 'general',
      comment: ''
    };

    this.currentGenericForm = {
      method: 'POST',
      successTitle: 'Comentario creado correctamente',
      errorTitle: 'Error al crear el comentario',
      fields: this.getCategoryFormFields(true),
      name: 'comentario',
      createButtonTitle: '',
      cols: 2,
      createApiSlug: '/student-comments',
      formTitle: 'Nuevo Comentario',
      formButtonTitle: 'Guardar',
      width: '2xl'
    };

    this.showGenericDialog = true;
  }

  openEditModal(comment: StudentComment): void {
    this.editingComment = comment;
    this.genericFormInitialData = {
      comment: comment.comment,
      category: comment.category
    };

    this.currentGenericForm = {
      method: 'PUT',
      successTitle: 'Comentario actualizado correctamente',
      errorTitle: 'Error al actualizar el comentario',
      fields: this.getCategoryFormFields(false),
      name: 'comentario',
      createButtonTitle: '',
      cols: 2,
      createApiSlug: '',
      updateApiSlug: `/student-comments/${comment.id}`,
      formTitle: 'Editar Comentario',
      formButtonTitle: 'Actualizar',
      width: '2xl'
    };

    this.showGenericDialog = true;
  }

  closeModal(): void {
    this.showGenericDialog = false;
    this.currentGenericForm = null;
    this.genericFormInitialData = null;
    this.editingComment = null;
  }

  handleFormSuccess(response: any): void {
    this.loadComments();
    this.closeModal();
  }

  deleteComment(comment: StudentComment): void {
    if (!confirm('¿Estás seguro de que deseas eliminar este comentario?')) {
      return;
    }

    this.commentService.deleteComment(comment.id).subscribe({
      next: (response) => {
        if (response.success) {
          this.toastr.success('Comentario eliminado correctamente', 'Éxito');
          this.loadComments();
        }
      },
      error: (error) => {
        console.error('Error deleting comment:', error);
        this.toastr.error('Error al eliminar el comentario', 'Error');
      }
    });
  }

  getCategoryClass(category: string): string {
    const classes: { [key: string]: string } = {
      'clase_teorica': 'bg-red-100 text-red-800',
      'clase_practica': 'bg-red-200 text-red-900',
      'examen': 'bg-red-300 text-red-900',
      'general': 'bg-gray-100 text-gray-800'
    };
    return classes[category] || classes['general'];
  }

  canEdit(comment: StudentComment): boolean {
    return comment.can_edit && comment.teacher_id === this.currentUserId;
  }
}
